import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { getIncludedOpenShiftsData } from "@src/appV2/redesign/Shift/Open/getIncludedOpenShiftsData";
import { type OpenShift, type OpenShiftWorkplace } from "@src/appV2/redesign/Shift/Open/types";
import { useGetOpenShifts } from "@src/appV2/redesign/Shift/Open/useGetOpenShifts";
import { useWorkerGeoLocation } from "@src/appV2/Worker/useWorkerGeoLocation";
import { addMinutes, isValid, parseISO, subMinutes } from "date-fns";
import { useMemo } from "react";

import {
  FCF_SHIFT_RECOMMENDATIONS_DEFAULT_CONFIG,
  type FcfDeepLinkParams,
  MAX_FCF_REPLACEMENT_SHIFTS,
} from "../types";

interface UseGetFcfReplacementShiftsParams extends FcfDeepLinkParams {
  enabled: boolean;
}

interface FcfReplacementShift {
  shift: OpenShift;
  workplace: OpenShiftWorkplace;
}

interface UseGetFcfReplacementShiftsResult {
  replacementShifts: FcfReplacementShift[];
  isLoading: boolean;
  isSuccess: boolean;
}

export function useGetFcfReplacementShifts(
  params: UseGetFcfReplacementShiftsParams
): UseGetFcfReplacementShiftsResult {
  const { qualification, shiftStart, cancelledWorkplaceId, enabled } = params;

  const fcfConfig = useCbhFlag(CbhFeatureFlag.FCF_SHIFT_RECOMMENDATIONS, {
    defaultValue: FCF_SHIFT_RECOMMENDATIONS_DEFAULT_CONFIG,
  });

  const workerGeoLocation = useWorkerGeoLocation();

  const shiftStartDate = parseISO(shiftStart);
  const isValidStartDate = isValid(shiftStartDate);
  const startGte = isValidStartDate
    ? subMinutes(shiftStartDate, fcfConfig.startProximityMinutes).toISOString()
    : "";
  const startLte = isValidStartDate
    ? addMinutes(shiftStartDate, fcfConfig.startProximityMinutes).toISOString()
    : "";

  const isQueryEnabled = enabled && isValidStartDate && Boolean(workerGeoLocation);

  const query = useGetOpenShifts(
    {
      filter: {
        start: [{ gte: startGte, lte: startLte }],
        area: {
          latitude: workerGeoLocation?.latitude ?? 0,
          longitude: workerGeoLocation?.longitude ?? 0,
          radiusInMiles: fcfConfig.distanceMiles,
        },
      },
      include: ["workplace"],
      sort: ["distance", "startAt"],
    },
    { enabled: isQueryEnabled }
  );

  const replacementShifts = useMemo<FcfReplacementShift[]>(() => {
    if (!query.data) {
      return [];
    }

    const { workplacesMap } = getIncludedOpenShiftsData(query.data);

    return query.data.data
      .filter(
        (shift) =>
          shift.attributes.qualification === qualification &&
          shift.relationships.workplace.data.id !== cancelledWorkplaceId
      )
      .slice(0, MAX_FCF_REPLACEMENT_SHIFTS)
      .flatMap((shift) => {
        const workplace = workplacesMap.get(shift.relationships.workplace.data.id);
        return workplace ? [{ shift, workplace }] : [];
      });
  }, [query.data, qualification, cancelledWorkplaceId]);

  return {
    replacementShifts,
    isLoading: isQueryEnabled && query.isLoading,
    isSuccess: query.isSuccess,
  };
}
